import React, { useRef } from "react";
import { Mic, Pause, ArrowUp, ArrowDown, Minus, ShieldCheck, Play } from "lucide-react";
import CallPlayer, { isPlayable, playHref, stamp } from "./CallPlayer.jsx";

// Where the call turned, and what the rep's voice did there.
//
// A score tells somebody they got 4 on objection handling. It does not tell
// them WHERE. The rep then has to re-listen to forty minutes to find the
// thirty seconds the number is about, which means they don't, which means the
// number changes nothing. Feedback that cannot be located is not feedback.
//
// Every row here is timestamped and, where the recording can be embedded,
// plays the call at that second.
//
// ── Why the voice reading is the point ────────────────────────────────────
//
// Each moment carries a transcript fact ("the buyer pushed back") and a
// DELIVERY fact ("and your pitch rose 20% above your own normal"). The second
// one is the coaching. Two reps can say identical words to an objection and
// one of them sounds certain; a transcript cannot tell them apart, and a
// transcript-only tool structurally cannot produce this section at all.
//
// ── Pitch is relative to the speaker, never absolute ──────────────────────
//
// Raw f0 is confounded by sex and voice type: scoring "energy" off absolute Hz
// builds a scorer that reads lower voices as flat and higher ones as animated,
// against named people whose probation depends on it. Every number here is a
// RATIO to that speaker's own median pitch within the same call, so "20% above
// your normal" means the same thing for every voice. Rule 1 of CallCard still
// applies: the Hz never reaches the screen, only the behaviour it implies.

const card = {
  background: "var(--surface)", border: "1px solid var(--c-200)",
  borderRadius: 14, padding: "16px 18px",
};
const muted = { color: "var(--c-500)" };

// Below this a change is not audible as one, and reporting it would invite a
// rep to hear something that is not there.
const NOTABLE = 0.12;
// Loudness drifts far more than pitch between ordinary sentences (mic
// distance, the end of a breath, a phone's gain control), so it only counts
// as a change past about 3 dB. Same value as ECHO key_moments.LOUDNESS_NOTABLE.
const LOUD_NOTABLE = 0.35;

// Kinds no longer shown. A pause before answering is what the rubric's 10
// asks for ("a beat of silence, then the answer"); flagging it as uncertainty
// contradicted the standard, and ECHO no longer emits it. Filtered here so
// cards scored before that change stop showing it too.
export const RETIRED_KINDS = new Set(["hesitation"]);

export const KIND_LABEL = {
  objection: "They pushed back",
  price: "Price came up",
  commitment: "A next step was named",
  concession: "They agreed with your framing",
  buyer_question: "They asked about what you're selling",
  buyer_interest: "They showed interest",
  buyer_next_step: "They offered a next step",
  hesitation: "You paused before answering",
  monologue: "You spoke without a break",
  disengagement: "They stopped participating",
};

// Same rule as CallCard's own Timestamp: a control that seeks and a control
// that opens a tab are different affordances and must not look identical.
export function Timestamp({ atSeconds, label, callLink, onSeek }) {
  if (atSeconds == null) {
    return <span style={{ ...muted, fontSize: 12 }}>{label}</span>;
  }
  const shared = {
    fontSize: 12, fontVariantNumeric: "tabular-nums", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 3,
  };
  if (isPlayable(callLink)) {
    return (
      <button type="button" onClick={() => onSeek && onSeek(atSeconds)}
              title={`Play from ${label}`}
              style={{ ...shared, border: "none", background: "transparent", cursor: "pointer",
                       padding: 0, fontFamily: "inherit", color: "var(--blue-600)", fontWeight: 600 }}>
        <Play size={10} />{label}
      </button>
    );
  }
  const href = playHref(callLink, atSeconds);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" style={{ ...shared, color: "var(--blue-600)" }}>
      <Play size={10} />{label}
    </a>
  ) : (
    <span style={{ ...muted, fontSize: 12 }}>{label}</span>
  );
}

// How far off normal a reading has to be before the row itself is worth
// drawing attention to. Shared so the chip, the row wash and the chart cannot
// disagree about what counts as notable.
export function deviationOf(voice) {
  if (!voice) return 0;
  const pitch = voice.pitch_vs_baseline != null ? voice.pitch_vs_baseline - 1 : 0;
  const loudRaw = voice.loudness_vs_baseline != null ? voice.loudness_vs_baseline - 1 : 0;
  const loud = Math.abs(loudRaw) >= LOUD_NOTABLE ? loudRaw : 0;
  return Math.abs(loud) > Math.abs(pitch) ? loud : pitch;
}

function VoiceChip({ voice, isRep = true }) {
  // Only the rep's own turns carry a voice reading, so a buyer moment without
  // one is normal, not a gap. Saying "delivery not measured on this call"
  // under it contradicted the voice section right above it.
  if (!voice && !isRep) return null;
  if (!voice) {
    return (
      <span style={{ ...muted, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5 }}>
        <Mic size={12} /> your voice wasn't measured at this moment
      </span>
    );
  }
  // The arrow has to agree with the sentence beside it. Reading the direction
  // from pitch ALONE printed "louder than usual" next to a flat grey dash,
  // because that reading came from energy while the pitch sat inside its
  // normal band -- the chip contradicted its own text.
  const deviation = deviationOf(voice);
  const up = deviation >= NOTABLE;
  const down = deviation <= -NOTABLE;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  // Off-normal is the thing worth finding, so only it gets colour. A steady
  // delivery is the background state and must not compete with it.
  //
  // Only the REP's own delivery is coloured as something to act on. The
  // buyer's voice rising is information, often a good sign, and printing it
  // in the same alarm red as the rep's own wobble told somebody to go fix
  // a turn they did not speak.
  const tone = !isRep ? "var(--c-500)"
    : up ? "var(--red-600, #d03b3b)"
      : down ? "var(--blue-600)" : "var(--c-500)";
  return (
    <span style={{
      fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5,
      color: tone, fontWeight: isRep && (up || down) ? 650 : 400,
    }}>
      <Icon size={12} />{voice.reading}
    </span>
  );
}

// `onSeek` drives the call card's own player when the page has one. Without
// it this section used to mount a SECOND player for the same recording further
// down the page, so a timestamp here played audio nowhere near the quote the
// reader had just clicked from above.
export default function KeyMoments({ call, onSeek }) {
  const moments = (call?.key_moments || []).filter((m) => !RETIRED_KINDS.has(m.kind));
  const provenance = call?.scored_with || {};
  const playerRef = useRef(null);
  const seek = onSeek || ((seconds) => playerRef.current?.seek(seconds));

  if (!moments.length) {
    return (
      <div style={card}>
        <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".04em", color: "var(--c-600)", marginBottom: 6 }}>
          Where the call turned
        </div>
        <p style={{ ...muted, fontSize: 13, margin: 0 }}>
          No turning points on this call: no push-back, no price talk, no next step agreed.
        </p>
      </div>
    );
  }

  const withVoice = moments.filter((m) => m.voice).length;
  const playable = isPlayable(call?.call_link);

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".04em", color: "var(--c-600)" }}>
          Where the call turned
        </div>
        <span style={{ ...muted, fontSize: 11.5 }}>
          {moments.length} moment{moments.length === 1 ? "" : "s"}
          {withVoice ? ` · ${withVoice} with a voice reading` : " · no audio, so no delivery reading"}
        </span>
      </div>

      <div style={{ margin: "10px -4px 0" }}>
        {moments.map((m, i) => {
          // Only the rep's own off-normal turns get the wash -- see VoiceChip.
          const off = m.who === "rep" && Math.abs(deviationOf(m.voice)) >= NOTABLE;
          return (
            <div key={`${m.at_seconds}-${i}`} style={{
              display: "grid", gridTemplateColumns: "58px 1fr", gap: 12,
              padding: "11px 4px", borderTop: i ? "1px solid var(--c-200)" : "none",
              // The moment the delivery slipped is the one worth finding; a
              // faint wash is enough to make the eye land there without
              // shouting at somebody about their own voice.
              background: off ? "var(--c-050, rgba(127,127,127,.05))" : "transparent",
              borderRadius: off ? 8 : 0,
            }}>
              <div style={{ paddingTop: 1 }}>
                <Timestamp atSeconds={m.at_seconds} label={m.at || stamp(m.at_seconds)}
                           callLink={call.call_link} onSeek={seek} />
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", gap: 7, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em",
                    padding: "2px 6px", borderRadius: 5,
                    background: m.who === "rep" ? "var(--blue-050, rgba(42,120,214,.12))" : "var(--c-100)",
                    color: m.who === "rep" ? "var(--blue-600)" : "var(--c-600)",
                  }}>{m.who === "rep" ? "you" : "them"}</span>
                  <strong style={{ fontSize: 13.5 }}>
                    {m.title || KIND_LABEL[m.kind] || m.kind}
                  </strong>
                </div>

                {m.said && (
                  <p style={{ margin: "5px 0 6px", fontSize: 13, color: "var(--c-800)",
                              borderLeft: "2px solid var(--c-200)", paddingLeft: 10 }}>
                    “{m.said}”
                  </p>
                )}

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
                  {m.pause_before_s ? (
                    <span style={{ ...muted, fontSize: 11.5, display: "inline-flex",
                                   alignItems: "center", gap: 5 }}>
                      <Pause size={12} />{m.pause_before_s}s of silence first
                    </span>
                  ) : null}
                  <VoiceChip voice={m.voice} isRep={m.who === "rep"} />
                </div>

                {m.matters_because && (
                  <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>{m.matters_because}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {playable && !onSeek && (
        <CallPlayer ref={playerRef} link={call.call_link} label={call.call_label} />
      )}

      {/* Which rubric and which model produced this. Two scores sharing a
          version are comparable; two that do not are not — and a rep looking
          at their own trend deserves to see when the ruler changed rather than
          reading it as their own movement. */}
      {(provenance.model_id || provenance.rubric_version) && (
        <div style={{ ...muted, fontSize: 11, marginTop: 12, paddingTop: 10,
                      borderTop: "1px solid var(--c-200)", display: "flex",
                      alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <ShieldCheck size={12} />
          Scored against rubric <code>{provenance.rubric_version || "—"}</code>
          {provenance.model_id ? <> by <code>{provenance.model_id}</code></> : null}
          {provenance.had_audio === false
            ? " · no recording was available, so delivery was not judged"
            : provenance.had_audio === true ? " · with the recording" : null}
        </div>
      )}
    </div>
  );
}
